import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ChevronDown, Calendar } from "lucide-react";

const formatDateValue = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDateValue = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

/* A native <select> popup can't have its height capped, so a 100-entry year
 * list fills the viewport. This is a DOM listbox: short and scrollable. */
function SelectMenu({ value, options, onSelect, triggerClass, ariaLabel }) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Open onto the current value, not the top of the list.
  useEffect(() => {
    if (!isOpen) return;
    listRef.current
      ?.querySelector('[data-selected="true"]')
      ?.scrollIntoView({ block: "center" });
  }, [isOpen]);

  const selected = options.find((option) => option.value === value);

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={triggerClass}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {selected?.label ?? ""}
        <ChevronDown
          className={`w-3 h-3 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div
          ref={listRef}
          role="listbox"
          style={{ colorScheme: "var(--color-scheme)" }}
          className="absolute z-30 mt-1 max-h-44 w-full overflow-y-auto rounded-md border border-(--color-border) bg-(--color-surface) py-1 shadow-[0_10px_24px_rgba(0,0,0,0.35)]"
        >
          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                data-selected={isSelected}
                onClick={() => {
                  onSelect(option.value);
                  setIsOpen(false);
                }}
                className={`block w-full px-2 py-1 text-left text-xs transition-colors ${
                  isSelected
                    ? "bg-(--color-primary) text-(--color-text-on-primary)"
                    : "text-(--color-text-primary) hover:bg-(--color-primary)/10"
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function DatePicker({
  label,
  name,
  value,
  onChange,
  placeholder = "",
  required = false,
  margin = "mb-4",
  minYear,
  maxYear,
  helperText,
  size = "default",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedDate = useMemo(() => parseDateValue(value), [value]);
  const [viewDate, setViewDate] = useState(selectedDate || new Date());

  useEffect(() => {
    if (selectedDate) {
      setViewDate(selectedDate);
    }
  }, [selectedDate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const locale = document.documentElement.lang || "en";
  const isRTL = document.documentElement.dir === "rtl";
  const today = new Date();

  const weekdays = Array.from({ length: 7 }, (_, index) => {
    const baseDate = new Date(2021, 7, 1 + index); // Sunday start
    return new Intl.DateTimeFormat(locale, { weekday: "short" })
      .format(baseDate)
      .slice(0, 2);
  });

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const fallbackMinYear = today.getFullYear() - 100;
  const fallbackMaxYear = today.getFullYear() + 10;
  const resolvedMinYear = minYear ?? fallbackMinYear;
  const resolvedMaxYear = maxYear ?? fallbackMaxYear;
  const hasMinYear = minYear != null;
  const hasMaxYear = maxYear != null;
  const normalizedMinYear = Math.min(resolvedMinYear, resolvedMaxYear);
  const normalizedMaxYear = Math.max(resolvedMinYear, resolvedMaxYear);
  const yearRangeMin = hasMinYear
    ? normalizedMinYear
    : Math.min(normalizedMinYear, year);
  const yearRangeMax = hasMaxYear
    ? normalizedMaxYear
    : Math.max(normalizedMaxYear, year);
  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) => ({
        value: index,
        label: new Intl.DateTimeFormat(locale, { month: "short" }).format(
          new Date(2021, index, 1),
        ),
      })),
    [locale],
  );
  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = yearRangeMax; y >= yearRangeMin; y -= 1) {
      years.push({ value: y, label: String(y) });
    }
    return years;
  }, [yearRangeMax, yearRangeMin]);
  const startOfMonth = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startDay = startOfMonth.getDay();

  const days = [];
  for (let i = 0; i < startDay; i += 1) {
    days.push(null);
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    days.push(new Date(year, month, day));
  }

  const handleSelect = (date) => {
    if (!date) return;
    const nextValue = formatDateValue(date);
    onChange({ target: { name, value: nextValue } });
    setIsOpen(false);
  };

  const isSameDay = (a, b) => a && b && a.toDateString() === b.toDateString();

  useEffect(() => {
    if (!hasMinYear && !hasMaxYear) return;
    if (year < normalizedMinYear) {
      setViewDate(new Date(normalizedMinYear, month, 1));
    } else if (year > normalizedMaxYear) {
      setViewDate(new Date(normalizedMaxYear, month, 1));
    }
  }, [
    hasMaxYear,
    hasMinYear,
    month,
    normalizedMaxYear,
    normalizedMinYear,
    year,
  ]);

  const canGoPrev =
    !hasMinYear ||
    year > normalizedMinYear ||
    (year === normalizedMinYear && month > 0);
  const canGoNext =
    !hasMaxYear ||
    year < normalizedMaxYear ||
    (year === normalizedMaxYear && month < 11);

  const handleMonthChange = (nextMonth) => {
    setViewDate(new Date(year, nextMonth, 1));
  };

  const handleYearChange = (nextYear) => {
    setViewDate(new Date(nextYear, month, 1));
  };

  const inputClasses =
    size === "small"
      ? `w-full px-3 py-2 rounded-lg focus:outline-none text-(--color-text-primary) text-sm ${
          isRTL ? "pr-10 pl-3" : "pl-10 pr-3"
        }` +
        " bg-(--color-bg-card) border border-(--color-border)" +
        " focus:border-(--color-primary) focus:ring-2 focus:ring-(--color-primary)" +
        " placeholder-(--color-text-muted)"
      : `w-full h-10 rounded-lg focus:outline-none text-[13px] text-(--color-text-primary) ${
          isRTL ? "pr-10 pl-10" : "pl-10 pr-10"
        }` +
        " bg-(--color-bg-card) border-2 border-(--color-border)" +
        " focus:border-(--color-primary) focus:shadow-[0_0_8px_var(--color-shadow-primary)]" +
        " placeholder-[var(--color-text-muted)] placeholder:text-[13px]";
  const selectorClasses =
    "flex h-7 items-center justify-between gap-1 rounded-md border border-(--color-border) bg-(--color-surface) px-2 text-xs text-(--color-text-primary)" +
    " focus:outline-none focus:border-(--color-primary) focus:shadow-[0_0_6px_var(--color-shadow-primary)]";

  return (
    <div className={margin} ref={dropdownRef}>
      {label && (
        <label
          className={`block font-medium ${size === "small" ? "text-xs mb-1.5 text-(--color-text-primary)" : "text-sm mb-2 text-(--color-text-secondary)"} ${isRTL ? "text-right" : "text-left"}`}
        >
          {label}
          {required && <span className="required-asterisk" />}
        </label>
      )}

      <div className="relative">
        <div
          className={`absolute ${isRTL ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 pointer-events-none`}
        >
          <Calendar
            className={`${size === "small" ? "w-4 h-4" : "w-5 h-5"} text-(--color-primary)`}
          />
        </div>
        <input
          type="text"
          name={name}
          value={selectedDate ? selectedDate.toLocaleDateString() : ""}
          placeholder={placeholder}
          readOnly
          required={required}
          onClick={() => setIsOpen(true)}
          onFocus={() => setIsOpen(true)}
          className={inputClasses}
        />
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className={`absolute ${isRTL ? "left-3" : "right-3"} top-1/2 -translate-y-1/2 text-(--color-text-muted) hover:text-(--color-primary) transition-colors`}
          aria-label="Toggle calendar"
        >
          <ChevronDown
            className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </button>

        {isOpen && (
          <div
            className="absolute z-20 mt-2 w-full rounded-lg border border-(--color-border) bg-(--color-bg) shadow-[0_10px_24px_rgba(0,0,0,0.12)]"
            dir={isRTL ? "rtl" : "ltr"}
          >
            <div className="flex items-center justify-between px-2 py-1.5 border-b border-(--color-border)">
              <button
                type="button"
                onClick={() => setViewDate(new Date(year, month - 1, 1))}
                className={`p-1 rounded-md transition-colors text-(--color-text-muted) ${
                  canGoPrev
                    ? "hover:text-(--color-text-primary) hover:bg-(--color-bg-darker)"
                    : "opacity-40 cursor-not-allowed"
                }`}
                disabled={!canGoPrev}
                aria-label="Previous month"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-2">
                <SelectMenu
                  value={month}
                  options={monthOptions}
                  onSelect={handleMonthChange}
                  triggerClass={selectorClasses}
                  ariaLabel="Select month"
                />
                <SelectMenu
                  value={year}
                  options={yearOptions}
                  onSelect={handleYearChange}
                  triggerClass={selectorClasses}
                  ariaLabel="Select year"
                />
              </div>
              <button
                type="button"
                onClick={() => setViewDate(new Date(year, month + 1, 1))}
                className={`p-1 rounded-md transition-colors text-(--color-text-muted) ${
                  canGoNext
                    ? "hover:text-(--color-text-primary) hover:bg-(--color-bg-darker)"
                    : "opacity-40 cursor-not-allowed"
                }`}
                disabled={!canGoNext}
                aria-label="Next month"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 px-2 pt-2 text-[11px] text-(--color-text-muted)">
              {weekdays.map((weekday) => (
                <div key={weekday} className="text-center">
                  {weekday}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1 px-2 pb-2 pt-1 text-xs">
              {days.map((date, index) => {
                if (!date) {
                  return <div key={`empty-${index}`} className="h-7" />;
                }

                const isSelected = isSameDay(date, selectedDate);
                const isToday = isSameDay(date, today);

                return (
                  <button
                    key={date.toISOString()}
                    type="button"
                    onClick={() => handleSelect(date)}
                    className={`h-7 rounded-md transition-colors ${
                      isSelected
                        ? "bg-(--color-primary) text-(--color-text-on-primary)"
                        : isToday
                          ? "border border-(--color-primary) text-(--color-primary)"
                          : "text-(--color-text-primary) hover:bg-(--color-primary)/10"
                    }`}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {helperText && (
        <p className="mt-2 text-xs text-(--color-text-muted)">{helperText}</p>
      )}
    </div>
  );
}
